import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight, CalendarClock, ClipboardList, Wheat, Store, Clock, ShoppingBag,
  NotebookPen, Sparkles, CheckCircle2, ChefHat, Menu, X
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1590301157172-7ba48dd1c2b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxhcnRpc2FuJTIwc291cmRvdWdoJTIwYnJlYWQlMjBydXN0aWN8ZW58MHx8fHwxNzg3NTg0MDE1fDA&ixlib=rb-4.1.0&q=85";
const BAKERY_IMG = "https://images.unsplash.com/photo-1738866299755-6ef45d3fc01e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxiYWtlciUyMGtpdGNoZW4lMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzg3NTg0MDE1fDA&ixlib=rb-4.1.0&q=85";
const CROISSANT_IMG = "https://images.unsplash.com/photo-1555507036-ab1f4038808a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxjcm9pc3NhbnRzJTIwcGFzdHJ5JTIwYmVhdXRpZnVsfGVufDB8fHx8MTc4NzU4NDAxNXww&ixlib=rb-4.1.0&q=85";

function Nav() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-cocoa/5 bg-cream/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
        <Logo />
        <nav className="hidden items-center gap-8 text-sm font-medium text-cocoa/70 md:flex">
          <a href="#how" className="transition-colors hover:text-terracotta">How it works</a>
          <a href="#features" className="transition-colors hover:text-terracotta">Features</a>
          <a href="#pricing" className="transition-colors hover:text-terracotta">Pricing</a>
          <Link to="/bakery/honey-and-hearth" className="transition-colors hover:text-terracotta">Sample bakery</Link>
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          {user ? (
            <Link to="/dashboard"><Button className="rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="nav-dashboard-btn">Dashboard</Button></Link>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-cocoa/70 hover:text-terracotta" data-testid="nav-login-link">Sign in</Link>
              <Link to="/register"><Button className="rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="nav-start-btn">Start your bakery</Button></Link>
            </>
          )}
        </div>
        <button className="md:hidden text-cocoa" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="border-t border-cocoa/5 bg-cream px-5 py-4 md:hidden">
          <div className="flex flex-col gap-4 text-sm font-medium text-cocoa/80">
            <a href="#how" onClick={() => setOpen(false)}>How it works</a>
            <a href="#features" onClick={() => setOpen(false)}>Features</a>
            <a href="#pricing" onClick={() => setOpen(false)}>Pricing</a>
            <Link to="/bakery/honey-and-hearth">Sample bakery</Link>
            <div className="flex gap-3 pt-2">
              <Link to="/login" className="flex-1"><Button variant="outline" className="w-full rounded-full">Sign in</Button></Link>
              <Link to="/register" className="flex-1"><Button className="w-full rounded-full bg-terracotta text-warmwhite">Start</Button></Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function ProductPreview() {
  return (
    <div className="relative animate-fade-up" style={{ animationDelay: "0.2s" }}>
      <div className="rounded-[28px] border border-cocoa/10 bg-warmwhite p-4 shadow-[0_24px_60px_rgba(64,53,47,0.12)]">
        <div className="mb-4 flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-full bg-terracotta/15" />
            <div>
              <p className="font-serif text-lg leading-none text-cocoa">Honey &amp; Hearth</p>
              <p className="text-xs text-cocoa/50">Overview</p>
            </div>
          </div>
          <span className="rounded-full bg-sage/25 px-3 py-1 text-xs font-medium text-cocoa">Open for orders</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[["Orders", "14", CalendarClock], ["Items sold", "38", ShoppingBag], ["Gross", "$472", Sparkles]].map(([l, v, Icon]) => (
            <div key={l} className="rounded-2xl bg-cream p-3">
              <Icon strokeWidth={1.5} className="mb-2 h-4 w-4 text-terracotta" />
              <p className="font-serif text-2xl leading-none text-cocoa">{v}</p>
              <p className="mt-1 text-[11px] text-cocoa/50">{l}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 rounded-2xl bg-cream p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-cocoa/50">Saturday Sourdough Drop</p>
          {[["Country Sourdough", "12/20"], ["Cinnamon Rolls", "8/12"], ["Choc Chip Cookies", "15/15"]].map(([n, c], i) => (
            <div key={n} className="flex items-center justify-between border-t border-cocoa/5 py-2 first:border-0">
              <span className="text-sm text-cocoa">{n}</span>
              <span className={`text-xs font-medium ${i === 2 ? "text-terracotta" : "text-cocoa/50"}`}>{i === 2 ? "Sold out" : `${c} sold`}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute -bottom-6 -left-6 hidden rotate-[-4deg] rounded-2xl border border-cocoa/10 bg-warmwhite p-3 shadow-lg sm:block">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-cocoa/50">Ingredient totals</p>
        <p className="font-serif text-lg text-cocoa">5,400g flour</p>
        <p className="text-xs text-cocoa/50">for 12 loaves</p>
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-cream paper-grain">
      <Nav />

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-5 pb-16 pt-14 sm:pt-20">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-sage/20 px-4 py-1.5 text-xs font-medium text-cocoa">
              <ChefHat strokeWidth={1.5} className="h-3.5 w-3.5" /> For home bakers &amp; cottage bakeries
            </span>
            <h1 className="mt-5 font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-cocoa sm:text-5xl lg:text-6xl">
              Your bakery,<br /><span className="text-terracotta">beautifully organized.</span>
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-cocoa/70">
              Manage preorders, pickup times, recipes, and bake days in one beautiful place.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => navigate(user ? "/dashboard" : "/register")} className="group rounded-full bg-terracotta px-6 py-6 text-base text-warmwhite hover:bg-terracotta/90" data-testid="hero-start-btn">
                Start your bakery
                <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button onClick={() => navigate("/bakery/honey-and-hearth")} variant="outline" className="rounded-full border-cocoa/20 bg-transparent px-6 py-6 text-base text-cocoa hover:bg-warmwhite" data-testid="hero-sample-btn">
                See a sample bakery
              </Button>
            </div>
            <p className="mt-4 text-xs text-cocoa/60">Start your 14-day free trial — no credit card required.</p>
          </div>
          <ProductPreview />
        </div>
      </section>

      {/* Problem */}
      <section className="border-y border-cocoa/5 bg-warmwhite">
        <div className="mx-auto max-w-4xl px-5 py-20 text-center">
          <NotebookPen strokeWidth={1.4} className="mx-auto mb-5 h-8 w-8 text-terracotta" />
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-cocoa sm:text-3xl lg:text-4xl">
            Running a bakery shouldn't mean living in your DMs.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-cocoa/70">
            When orders come in through Instagram messages, notebooks, spreadsheets, and text threads, it's easy to
            lose track of who ordered what — and how much flour you actually need. Oven &amp; Order gently gathers it
            all into one calm, organized place so you can focus on the baking.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="mx-auto max-w-6xl px-5 py-20">
        <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">How it works</p>
        <h2 className="mt-3 text-center font-serif text-2xl font-semibold tracking-tight text-cocoa sm:text-3xl lg:text-4xl">Three simple steps</h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            [Store, "Set up your bakery", "Add your bakery details and your products with photos, prices, and descriptions."],
            [CalendarClock, "Open a preorder window", "Create a bake day with pickup times and quantity limits, then share your bakery link."],
            [ClipboardList, "See everything in one place", "Watch orders, ingredient totals, and your bake-day schedule come together automatically."],
          ].map(([Icon, title, body], i) => (
            <div key={title} className="rounded-2xl border border-cocoa/10 bg-warmwhite p-7 shadow-[0_4px_20px_rgba(64,53,47,0.04)] transition-transform duration-300 hover:-translate-y-1">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-terracotta/12 text-terracotta">
                <Icon strokeWidth={1.5} className="h-5 w-5" />
              </div>
              <p className="mt-5 text-xs font-semibold text-terracotta">Step {i + 1}</p>
              <h3 className="mt-1 font-serif text-xl text-cocoa">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-cocoa/65">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-y border-cocoa/5 bg-warmwhite">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Everything you need</p>
              <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-cocoa sm:text-3xl lg:text-4xl">
                Thoughtful tools, none of the clutter.
              </h2>
              <div className="mt-8 grid gap-x-6 gap-y-5 sm:grid-cols-2">
                {[
                  [Store, "Beautiful ordering pages"],
                  [ShoppingBag, "Product quantity limits"],
                  [Clock, "Pickup dates & time windows"],
                  [ClipboardList, "Organized customer orders"],
                  [Wheat, "Recipe & ingredient tracking"],
                  [Sparkles, "Automatic production totals"],
                  [CalendarClock, "Bake-day timelines"],
                  [CheckCircle2, "Mobile-friendly access"],
                ].map(([Icon, label]) => (
                  <div key={label} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sage/20 text-cocoa">
                      <Icon strokeWidth={1.5} className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-medium leading-tight text-cocoa">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src={HERO_IMG} alt="Fresh sourdough" className="col-span-2 h-56 w-full rounded-2xl object-cover shadow-[0_12px_40px_rgba(64,53,47,0.1)]" />
              <img src={CROISSANT_IMG} alt="Pastries" className="h-40 w-full rounded-2xl object-cover" />
              <img src={BAKERY_IMG} alt="Bakery kitchen" className="h-40 w-full rounded-2xl object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Sample storefront */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <div className="overflow-hidden rounded-[28px] border border-cocoa/10 bg-gradient-to-br from-terracotta/10 to-sage/10">
          <div className="grid items-center gap-8 p-8 sm:p-12 lg:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Sample storefront</p>
              <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-cocoa sm:text-3xl lg:text-4xl">Meet Honey &amp; Hearth</h2>
              <p className="mt-4 max-w-md text-base leading-relaxed text-cocoa/70">
                Small-batch sourdough and weekend treats — sourdough loaves, cinnamon rolls, chocolate chip cookies,
                and rosemary focaccia. Explore a real customer ordering page to see exactly what your bakers' customers
                will experience.
              </p>
              <Link to="/bakery/honey-and-hearth">
                <Button className="mt-6 group rounded-full bg-cocoa px-6 py-6 text-warmwhite hover:bg-cocoa/90" data-testid="sample-storefront-btn">
                  Visit the sample storefront
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
            <img src={HERO_IMG} alt="Honey and Hearth" className="h-72 w-full rounded-2xl object-cover shadow-[0_16px_48px_rgba(64,53,47,0.14)]" />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-y border-cocoa/5 bg-warmwhite">
        <div className="mx-auto max-w-5xl px-5 py-20">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Pricing</p>
          <h2 className="mt-3 text-center font-serif text-2xl font-semibold tracking-tight text-cocoa sm:text-3xl lg:text-4xl">Simple, honest pricing</h2>
          <p className="mx-auto mt-3 max-w-md text-center text-sm text-cocoa/60">Every plan starts with a 14-day free trial — no credit card required to begin.</p>
          <div className="mx-auto mt-12 grid max-w-3xl gap-6 md:grid-cols-2">
            <div className="relative rounded-2xl border-2 border-terracotta bg-cream p-8">
              <span className="absolute -top-3 left-8 rounded-full bg-terracotta px-3 py-1 text-xs font-semibold text-warmwhite">Founding Baker</span>
              <div className="mt-2 flex items-end gap-1">
                <span className="font-serif text-5xl text-cocoa">$12</span>
                <span className="pb-2 text-sm text-cocoa/60">/ month</span>
              </div>
              <p className="mt-3 text-sm text-cocoa/65">For early bakery owners joining during our initial launch.</p>
              <Link to="/register"><Button className="mt-6 w-full rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="pricing-founding-btn">Start your bakery</Button></Link>
            </div>
            <div className="rounded-2xl border border-cocoa/10 bg-cream p-8">
              <span className="text-xs font-semibold uppercase tracking-wide text-cocoa/50">Standard</span>
              <div className="mt-2 flex items-end gap-1">
                <span className="font-serif text-5xl text-cocoa">$19</span>
                <span className="pb-2 text-sm text-cocoa/60">/ month</span>
              </div>
              <p className="mt-1 text-sm text-cocoa/50">or $180 per year</p>
              <p className="mt-3 text-sm text-cocoa/65">Everything you need to run weekly preorder drops.</p>
              <Link to="/register"><Button variant="outline" className="mt-6 w-full rounded-full border-cocoa/20 text-cocoa hover:bg-warmwhite" data-testid="pricing-standard-btn">Get started</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-4xl px-5 py-24 text-center">
        <Wheat strokeWidth={1.3} className="mx-auto mb-5 h-9 w-9 text-terracotta" />
        <h2 className="font-serif text-3xl font-semibold tracking-tight text-cocoa sm:text-4xl lg:text-5xl">
          Ready to organize your next bake day?
        </h2>
        <p className="mx-auto mt-4 max-w-lg text-base text-cocoa/70">
          Create your bakery, add your products, and open your first preorder window in minutes.
        </p>
        <Button onClick={() => navigate(user ? "/dashboard" : "/register")} className="group mt-8 rounded-full bg-terracotta px-8 py-6 text-base text-warmwhite hover:bg-terracotta/90" data-testid="cta-start-btn">
          Create your bakery
          <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Button>
      </section>

      {/* Footer */}
      <footer className="border-t border-cocoa/10 bg-warmwhite">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 px-5 py-10 sm:flex-row">
          <Logo />
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-cocoa/60">
            <a href="mailto:ovenandorder@gmail.com" className="hover:text-terracotta">ovenandorder@gmail.com</a>
            <Link to="/privacy" className="hover:text-terracotta">Privacy</Link>
            <Link to="/terms" className="hover:text-terracotta">Terms</Link>
          </div>
        </div>
        <p className="pb-8 text-center text-xs text-cocoa/40">© {new Date().getFullYear()} Oven &amp; Order. Made for independent bakers.</p>
      </footer>
    </div>
  );
}
